/**
 * 会话事件流（`GET /conversations/{id}/stream`，SSE）。
 *
 * # 为什么断线要立刻撤掉「进行中」
 *
 * openapi 那段描述把话说死了：**「这条流不会在没有事实时说『还在跑』——
 * 连接断了就是断了，前端据此撤掉所有『进行中』。」**
 *
 * 这不是保守，是这一屏的核心承诺：界面上任何一个「正在工作」都是一句
 * 关于此刻的断言，而断线之后我们**不知道**此刻怎么样。不知道就说不知道。
 *
 * # 为什么续传用 Last-Event-ID 而不是自己记
 *
 * 服务端把 `id:` 设成 sequence，浏览器的 EventSource 重连时会自动带上
 * `Last-Event-ID`。自己记一份游标再拼 `?cursor=`，等于把同一个事实
 * 存两处——而两份迟早分岔，分岔的表现是「补齐之后少了几条」，
 * 且没有任何东西会红。
 *
 * `?cursor=` 留着不是备份，是**首次连接**时用的：从某个已知位置接上，
 * 那时还没有 Last-Event-ID 可言。
 */
import { ES_OPEN } from '../../app/sse-revive'
import type { LinkState } from './session-state'

export type StreamEventKind = 'snapshot' | 'ledger' | 'state'

export interface StreamEvent {
  readonly kind: StreamEventKind
  /** 服务端给的 `id:`，即 sequence。心跳没有 id（它是注释行，不是事件）。 */
  readonly sequence: number | null
  readonly data: unknown
}

/** 构造流的 URL。**首次带 cursor，重连不带**——重连由浏览器带 Last-Event-ID。 */
export function streamUrl(conversationId: string, cursor: number | null): string {
  const base = `/api/v1/conversations/${encodeURIComponent(conversationId)}/stream`
  return cursor === null ? base : `${base}?cursor=${cursor}`
}

/**
 * `EventSource.readyState` 的三档，以及「浏览器是不是已经放弃了」这条判据。
 *
 * **定义在 `app/sse-revive.ts`，这里只是再导出。** 那一条判断现在有三个
 * 消费方（Lulu 会话流、全站 `/events`、动态中心 `/activity/stream`），
 * 各写一份的那天它们会分家，而分家的表现是「有一条流不会自己接回来」——
 * 没有任何东西会红。理由见 CLAUDE.md §8「抽出一个共享的东西，不等于用上了它」。
 *
 * 它说的是什么、以及那份 2026-09-01 的生产日志，都在 `sse-revive.ts` 顶上。
 */
export { ES_CLOSED, ES_CONNECTING, ES_OPEN, browserGaveUp } from '../../app/sse-revive'

/**
 * 连接状态 → 界面的 link 档。
 *
 * `EventSource.readyState` 只有三档，而我们要的判断只有一个：
 * **此刻还能不能相信「进行中」**。所以 CONNECTING 与 CLOSED 都归 down——
 * 重连中那一段同样没有新事实，把它画成 live 就是在骗人。
 */
export function linkStateFrom(readyState: number): LinkState {
  return readyState === ES_OPEN ? 'live' : 'down'
}

/**
 * 心跳不是业务事件。
 *
 * 服务端用 SSE 注释行做心跳，浏览器不会把它派发成 message，
 * 所以这里正常不会遇到。这个函数存在是为了让「心跳不推进游标」
 * 这条有个能红的落点——一旦有人把心跳当事件计数，游标会往前跳，
 * 而重连时那几条真事件就被跳过去了。
 */
export function advancesCursor(event: StreamEvent): boolean {
  return event.kind === 'ledger' && event.sequence !== null
}

/**
 * 收到一批事件之后的新游标。
 *
 * **只取 ledger 事件的最大 sequence。** snapshot 与 state 是投影，
 * 不是账本上的行——拿它们推进游标，重连时会从一个账本上不存在的位置续，
 * 而那时服务端只能从头给，或者给不出来。
 */
export function nextCursor(current: number | null, events: readonly StreamEvent[]): number | null {
  let out = current
  for (const e of events) {
    if (!advancesCursor(e)) continue
    const seq = e.sequence as number
    if (out === null || seq > out) out = seq
  }
  return out
}
